// state-io.js — State serialisation helpers: normalizeImportedSite + sanitizeImportedState.
// Extracted from script.js §CORE-SITE-PIPELINE (~237 lines).
//
// Imports (no cycles — state-io.js is a leaf module):
//   workload-defs.js  — label maps, option arrays
//   helpers.js        — normalisation + factory helpers
//   recalc.js         — normalizeExistingEnvironmentCustomLabels, normalizeArchitectureState,
//                       sanitizeVdcVaultMigrationSizing, normalizeSiteForEngagement
//   state.js          — defaultToggleState, defaultProtectionState
//   utils.js          — isUnstructuredDataWorkload

import {
    hypervisorLabels, cloudLabels, saasLabels, containerLabels, pluginLabels, agentLabels,
    veeamOneOptionLabels, tapeConnectivityTypeLabels,
    vb365RepoTypeOptions, vbrRepoMigrationSourceTypeOptions
} from './workload-defs.js';

import {
    normalizeWorkloadEntries,
    sanitizeImplementationMode, sanitizeToggleState,
    normalizeSiteWorkloads, isGeneratedSiteName
} from './helpers.js';

import {
    normalizeExistingEnvironment, normalizeExistingEnvironmentCustomLabels,
    normalizeArchitectureState, normalizeSiteForEngagement
} from './recalc.js';

import { defaultToggleState, defaultProtectionState, AppState } from './state.js';

import { isUnstructuredDataWorkload, normalizeEnvironmentCount } from './utils.js';

// ── Schema version ────────────────────────────────────────────────────────────
// Bumped to 27 for the source workload refactor (Phase 7, 2026-06-04).
// Breaking change: sites[].jobs and sites[].unprotectedWorkloads replaced by
// sites[].sourceWorkloads and sites[].repositories. No migration shim —
// files saved with schema < 27 are rejected with a clear user-facing message.
export const STATE_SCHEMA_VERSION = 27;

// ── Site normalisation ────────────────────────────────────────────────────────

export function normalizeImportedSite(rawSite) {
    if (!rawSite || typeof rawSite !== 'object') {
        return null;
    }

    const rawWorkloads = Array.isArray(rawSite.workloads) ? rawSite.workloads : [];
    const workloadsFromSite = normalizeSiteWorkloads(rawWorkloads);
    const workloadsFromHypervisors = normalizeWorkloadEntries(
        Array.isArray(rawSite.sourceHypervisors) ? rawSite.sourceHypervisors : [],
        'hypervisor',
        'count',
        hypervisorLabels
    ).map((item) => ({ value: item.hypervisor, count: item.count }));
    const workloadsFromCloud = normalizeWorkloadEntries(
        Array.isArray(rawSite.sourceCloudWorkloads) ? rawSite.sourceCloudWorkloads : [],
        'workload',
        'count',
        cloudLabels
    ).map((item) => ({ value: item.workload, count: item.count }));
    const workloadsFromSaas = normalizeWorkloadEntries(
        Array.isArray(rawSite.sourceSaasWorkloads) ? rawSite.sourceSaasWorkloads : [],
        'workload',
        'count',
        saasLabels
    ).map((item) => ({ value: item.workload, count: item.count }));
    const workloadsFromContainers = normalizeWorkloadEntries(
        Array.isArray(rawSite.sourceContainerWorkloads) ? rawSite.sourceContainerWorkloads : [],
        'workload',
        'count',
        containerLabels
    ).map((item) => ({ value: item.workload, count: item.count }));
    const workloadsFromPlugins = normalizeWorkloadEntries(
        Array.isArray(rawSite.sourcePlugins) ? rawSite.sourcePlugins : [],
        'plugin',
        'quantity',
        pluginLabels
    ).map((item) => ({ value: item.plugin, count: item.quantity }));
    const workloadsFromAgents = normalizeWorkloadEntries(
        Array.isArray(rawSite.sourceAgents) ? rawSite.sourceAgents : [],
        'agent',
        'count',
        agentLabels
    ).map((item) => ({ value: item.agent, count: item.count }));
    const workloadsFromUnstructuredData = normalizeSiteWorkloads(
        Array.isArray(rawSite.sourceUnstructuredData) ? rawSite.sourceUnstructuredData : []
    );

    return normalizeSiteForEngagement({
        name: typeof rawSite.name === 'string' ? rawSite.name : '',
        vdcVaultSourceStorageType: typeof rawSite.vdcVaultSourceStorageType === 'string'
            ? rawSite.vdcVaultSourceStorageType
            : (typeof rawSite.sourceStorageType === 'string' ? rawSite.sourceStorageType : ''),
        vdcVaultRepositoryType: typeof rawSite.vdcVaultRepositoryType === 'string'
            ? rawSite.vdcVaultRepositoryType
            : (typeof rawSite.repositoryType === 'string' ? rawSite.repositoryType : ''),
        vdcVaultTargetVault: typeof rawSite.vdcVaultTargetVault === 'string'
            ? rawSite.vdcVaultTargetVault
            : (typeof rawSite.targetVault === 'string' ? rawSite.targetVault : ''),
        existingEnvironment: normalizeExistingEnvironment(
            rawSite.existingEnvironment || rawSite.existingVeeamEnvironment || {}
        ),
        existingEnvironmentCustomLabels: normalizeExistingEnvironmentCustomLabels(
            rawSite.existingEnvironmentCustomLabels || rawSite.existingEnvironmentLabels || {}
        ),
        existingEnvironmentDisplayOrder: Array.isArray(rawSite.existingEnvironmentDisplayOrder) ? [...rawSite.existingEnvironmentDisplayOrder] : null,
        existingEnvironmentVbrVersions:  (rawSite.existingEnvironmentVbrVersions  && typeof rawSite.existingEnvironmentVbrVersions  === 'object') ? { ...rawSite.existingEnvironmentVbrVersions  } : undefined,
        existingEnvironmentVbrPlatforms: (rawSite.existingEnvironmentVbrPlatforms && typeof rawSite.existingEnvironmentVbrPlatforms === 'object') ? { ...rawSite.existingEnvironmentVbrPlatforms } : undefined,
        existingEnvironmentVbrHaRoles:   (rawSite.existingEnvironmentVbrHaRoles   && typeof rawSite.existingEnvironmentVbrHaRoles   === 'object') ? { ...rawSite.existingEnvironmentVbrHaRoles   } : undefined,
        existingEnvironmentVbrHaPairIds: (rawSite.existingEnvironmentVbrHaPairIds && typeof rawSite.existingEnvironmentVbrHaPairIds === 'object') ? { ...rawSite.existingEnvironmentVbrHaPairIds } : undefined,
        existingEnvironmentVroVersions:  (rawSite.existingEnvironmentVroVersions  && typeof rawSite.existingEnvironmentVroVersions  === 'object') ? { ...rawSite.existingEnvironmentVroVersions  } : undefined,
        existingEnvironmentVoneVersions: (rawSite.existingEnvironmentVoneVersions && typeof rawSite.existingEnvironmentVoneVersions === 'object') ? { ...rawSite.existingEnvironmentVoneVersions } : undefined,
        existingEnvironmentRepoPlatform:       (rawSite.existingEnvironmentRepoPlatform       === 'linux') ? 'linux' : 'windows',
        existingEnvironmentVb365ProxyPlatform: (rawSite.existingEnvironmentVb365ProxyPlatform === 'linux') ? 'linux' : 'windows',
        existingEnvironmentVb365RepoTypes: (() => {
            // Per-slot dict (new format)
            if (rawSite.existingEnvironmentVb365RepoTypes && typeof rawSite.existingEnvironmentVb365RepoTypes === 'object') {
                return { ...rawSite.existingEnvironmentVb365RepoTypes };
            }
            // Legacy migration: flat scalar → slot 0 when at least one repo was present
            const legacyType = rawSite.existingEnvironmentVb365RepoType;
            const repoCount = normalizeEnvironmentCount(rawSite.existingEnvironment?.vb365RepositoryServers);
            if (legacyType && vb365RepoTypeOptions.some((o) => o.value === legacyType) && repoCount > 0) {
                const dict = {};
                for (let i = 0; i < repoCount; i++) dict[i] = legacyType;
                return dict;
            }
            return {};
        })(),
        existingEnvironmentVb365RepoConsumedTbs: (() => {
            // Per-slot dict (new format)
            if (rawSite.existingEnvironmentVb365RepoConsumedTbs && typeof rawSite.existingEnvironmentVb365RepoConsumedTbs === 'object') {
                return { ...rawSite.existingEnvironmentVb365RepoConsumedTbs };
            }
            // Legacy migration: flat scalar → slot 0
            const legacyTb = rawSite.existingEnvironmentVb365RepoConsumedTb;
            const repoCount = normalizeEnvironmentCount(rawSite.existingEnvironment?.vb365RepositoryServers);
            if (typeof legacyTb === 'number' && legacyTb > 0 && repoCount > 0) {
                return { 0: legacyTb };
            }
            return {};
        })(),
        workloads: normalizeSiteWorkloads([
            ...workloadsFromSite,
            ...workloadsFromHypervisors,
            ...workloadsFromCloud,
            ...workloadsFromSaas,
            ...workloadsFromContainers,
            ...workloadsFromPlugins,
            ...workloadsFromUnstructuredData,
            ...workloadsFromAgents
        ]),
        // Restore named server instances and their version metadata
        veeamServers: Array.isArray(rawSite.veeamServers) ? rawSite.veeamServers.map((s) => ({ ...s })) : [],
        existingEnvironmentServerVersions: (rawSite.existingEnvironmentServerVersions && typeof rawSite.existingEnvironmentServerVersions === 'object')
            ? { ...rawSite.existingEnvironmentServerVersions }
            : undefined,
        // Source workloads (schema v27+) — totalCount + jobs[].
        // sureBackup/sureReplica are now first-class job types (not boolean flags).
        // Migrates all legacy shapes on import.
        sourceWorkloads: Array.isArray(rawSite.sourceWorkloads)
            ? rawSite.sourceWorkloads
                .filter((sw) => sw && typeof sw === 'object' && typeof sw.workloadValue === 'string')
                .map((sw) => {
                    const id      = Number.isInteger(sw.id) ? sw.id : AppState.nextSourceWorkloadId++;
                    const _validTypes = ['backup','replication','cdp','backupCopy','sureBackup','sureReplica'];

                    const _normaliseJob = (j, fallbackCount = 0) => {
                        // Handle old boolean-flag shape: { type, sureBackup, sureReplica, ... }
                        // Expand into separate job entries returned as an array.
                        const base = {
                            type:              _validTypes.includes(j.type) ? j.type : 'backup',
                            count:             Math.max(0, parseInt(j.count ?? fallbackCount, 10) || 0),
                            managedByServerId: typeof j.managedByServerId === 'string' ? j.managedByServerId : null,
                        };
                        const extras = [];
                        if (j.sureBackup)  extras.push({ type: 'sureBackup',  count: base.count, managedByServerId: base.managedByServerId });
                        if (j.sureReplica) extras.push({ type: 'sureReplica', count: base.count, managedByServerId: base.managedByServerId });
                        return [base, ...extras];
                    };

                    // ── Current shape: totalCount + jobs[] ──────────────────
                    if (typeof sw.totalCount === 'number' && Array.isArray(sw.jobs)) {
                        return {
                            id,
                            workloadValue: sw.workloadValue,
                            label:      typeof sw.label === 'string' ? sw.label : '',
                            totalCount: Math.max(0, parseInt(sw.totalCount, 10) || 0),
                            jobs: sw.jobs.filter((j) => j && typeof j === 'object').flatMap((j) => _normaliseJob(j)),
                        };
                    }

                    // ── Legacy flat shape: count + protection{} ─────────────
                    if (typeof sw.count === 'number' && sw.protection) {
                        const p  = sw.protection;
                        const tc = Math.max(0, parseInt(sw.count, 10) || 0);
                        const ms = typeof sw.managedByServerId === 'string' ? sw.managedByServerId : null;
                        const jobs = [];
                        for (const t of ['backup','replication','cdp','backupCopy']) {
                            if (p[t]) jobs.push({ type: t, count: tc, managedByServerId: ms });
                        }
                        if (p.sureBackup)  jobs.push({ type: 'sureBackup',  count: tc, managedByServerId: ms });
                        if (p.sureReplica) jobs.push({ type: 'sureReplica', count: tc, managedByServerId: ms });
                        return { id, workloadValue: sw.workloadValue, totalCount: tc, jobs };
                    }

                    // ── Legacy groups[] shape ───────────────────────────────
                    if (Array.isArray(sw.groups)) {
                        const totalCount = sw.groups.reduce((s, g) => s + Math.max(0, parseInt(g.count, 10) || 0), 0);
                        const jobs = sw.groups.flatMap((g) => {
                            const p  = g.protection || {};
                            const gc = Math.max(0, parseInt(g.count, 10) || 0);
                            const ms = typeof g.managedByServerId === 'string' ? g.managedByServerId : null;
                            const out = [];
                            for (const t of ['backup','replication','cdp','backupCopy']) {
                                if (p[t]) out.push({ type: t, count: gc, managedByServerId: ms });
                            }
                            if (p.sureBackup)  out.push({ type: 'sureBackup',  count: gc, managedByServerId: ms });
                            if (p.sureReplica) out.push({ type: 'sureReplica', count: gc, managedByServerId: ms });
                            return out;
                        });
                        return { id, workloadValue: sw.workloadValue, totalCount, jobs };
                    }

                    return { id, workloadValue: sw.workloadValue, totalCount: 0, jobs: [] };
                })
            : [],
        // Repositories (schema v27+) — Step 1 repository entries within a site
        repositories: Array.isArray(rawSite.repositories)
            ? rawSite.repositories
                .filter((r) => r && typeof r === 'object')
                .map((r) => ({
                    id:                Number.isInteger(r.id) ? r.id : AppState.nextRepositoryId++,
                    label:             typeof r.label === 'string' ? r.label : '',
                    type:              typeof r.type  === 'string' ? r.type  : '',
                    consumedTb:        Math.max(0, parseFloat(r.consumedTb) || 0),
                    managedByServerId: typeof r.managedByServerId === 'string' ? r.managedByServerId : null,
                }))
            : [],
        step2Only: rawSite.step2Only === true,
    });
}

// ── State sanitisation ────────────────────────────────────────────────────────

export function sanitizeImportedState(rawState) {
    if (!rawState || typeof rawState !== 'object') {
        throw new Error('Imported file does not contain a valid estimator payload.');
    }

    // Schema version check — clean break at v27 (source workload refactor).
    // Files saved before v27 used jobs[] + unprotectedWorkloads[] which are no longer
    // supported. No migration shim: the data shapes are incompatible.
    const importedVersion = typeof rawState.schemaVersion === 'number' ? rawState.schemaVersion : 0;
    if (importedVersion < STATE_SCHEMA_VERSION) {
        throw new Error(
            `This file was saved with an older version of the estimator (schema v${importedVersion}) ` +
            `and is no longer compatible with the current version (schema v${STATE_SCHEMA_VERSION}). ` +
            `Please re-create your estimate using the current tool.`
        );
    }

    const rawSites = Array.isArray(rawState.sites) ? rawState.sites : [];
    const importedSites = rawSites
        .map((site, index) => {
            const normalizedSite = normalizeImportedSite(site);
            if (!normalizedSite) {
                return null;
            }

            if (isGeneratedSiteName(normalizedSite.name, index)) {
                normalizedSite.name = '';
                normalizedSite.renameDraft = '';
            }

            return normalizedSite;
        })
        .filter((site) => site !== null);

    const rawHypervisors = Array.isArray(rawState.sourceHypervisors) ? rawState.sourceHypervisors : [];
    const sourceHypervisors = normalizeWorkloadEntries(rawHypervisors, 'hypervisor', 'count', hypervisorLabels);

    const rawCloudWorkloads = Array.isArray(rawState.sourceCloudWorkloads) ? rawState.sourceCloudWorkloads : [];
    const sourceCloudWorkloads = normalizeWorkloadEntries(rawCloudWorkloads, 'workload', 'count', cloudLabels);

    const rawSaasWorkloads = Array.isArray(rawState.sourceSaasWorkloads) ? rawState.sourceSaasWorkloads : [];
    const sourceSaasWorkloads = normalizeWorkloadEntries(rawSaasWorkloads, 'workload', 'count', saasLabels);

    const rawContainerWorkloads = Array.isArray(rawState.sourceContainerWorkloads) ? rawState.sourceContainerWorkloads : [];
    const sourceContainerWorkloads = normalizeWorkloadEntries(rawContainerWorkloads, 'workload', 'count', containerLabels);

    const rawPlugins = Array.isArray(rawState.sourcePlugins) ? rawState.sourcePlugins : [];
    const sourcePlugins = normalizeWorkloadEntries(rawPlugins, 'plugin', 'quantity', pluginLabels);

    const rawUnstructuredData = Array.isArray(rawState.sourceUnstructuredData) ? rawState.sourceUnstructuredData : [];
    const sourceUnstructuredData = normalizeSiteWorkloads(rawUnstructuredData)
        .filter((item) => isUnstructuredDataWorkload(item.value));

    const rawAgents = Array.isArray(rawState.sourceAgents) ? rawState.sourceAgents : [];
    const sourceAgents = normalizeWorkloadEntries(rawAgents, 'agent', 'count', agentLabels);

    const legacySiteCount = Math.max(1, parseInt(rawState.siteCount, 10) || 1);
    const legacySites = Array.from({ length: legacySiteCount }, (_, index) => ({
        name: '',
        workloads: index === 0
            ? normalizeSiteWorkloads([
                ...sourceHypervisors.map((item) => ({ value: item.hypervisor, count: item.count })),
                ...sourceCloudWorkloads.map((item) => ({ value: item.workload, count: item.count })),
                ...sourceSaasWorkloads.map((item) => ({ value: item.workload, count: item.count })),
                ...sourceContainerWorkloads.map((item) => ({ value: item.workload, count: item.count })),
                ...sourcePlugins.map((item) => ({ value: item.plugin, count: item.quantity })),
                ...sourceUnstructuredData,
                ...sourceAgents.map((item) => ({ value: item.agent, count: item.count }))
            ])
            : []
    }));

    const normalizedSites = importedSites.length ? importedSites : legacySites;
    const rawVeeamOneOptions = Array.isArray(rawState.veeamOneOptions) ? rawState.veeamOneOptions : [];
    const veeamOneOptions = rawVeeamOneOptions
        .filter((option) => typeof option === 'string' && Object.hasOwn(veeamOneOptionLabels, option));

    const backupVerificationRequired = rawState.backupVerificationRequired === 'no' ? 'no' : 'yes_limited';
    const replicationRequired = sanitizeToggleState(rawState.replicationRequired, defaultToggleState.replicationRequired);
    const sureBackupRequired = backupVerificationRequired !== 'no'
        ? sanitizeToggleState(rawState.sureBackupRequired, defaultProtectionState.sureBackupRequired)
        : 'no';

    return {
        projectName: typeof rawState.projectName === 'string' ? rawState.projectName : '',
        sites: normalizedSites.map((site) => normalizeSiteForEngagement(site)),
        // Unified canvas: engagementType is not applied on import — syncEngagementTypeBodyAttribute()
        // re-derives it from canvas flags at runtime.
        architectureState: normalizeArchitectureState(rawState.architectureState || rawState, normalizedSites.length),
        // Unified canvas: design is always required — not stored in state.
        upgradeHealthCheckReviewRequired: sanitizeToggleState(
            rawState.upgradeHealthCheckReviewRequired
                ?? (rawState.upgradeFullHealthCheck === 'yes'
                    ? 'yes'
                    : rawState.upgradeLiteHealthCheck === 'yes'
                        ? 'no'
                        : defaultToggleState.upgradeHealthCheckReviewRequired),
            defaultToggleState.upgradeHealthCheckReviewRequired
        ),
        implementationMode: sanitizeImplementationMode(rawState),
        veeamOneRequired: sanitizeToggleState(rawState.veeamOneRequired, defaultToggleState.veeamOneRequired),
        veeamOneOptions,
        backupVerificationRequired,
        sureBackupRequired,
        backupCopyRequired: sanitizeToggleState(rawState.backupCopyRequired, defaultToggleState.backupCopyRequired),
        replicationRequired,
        sureReplicaRequired: replicationRequired === 'yes'
            ? sanitizeToggleState(rawState.sureReplicaRequired, defaultProtectionState.sureReplicaRequired)
            : 'no',
        tapeIntegrationRequired: sanitizeToggleState(rawState.tapeIntegrationRequired, defaultToggleState.tapeIntegrationRequired),
        tapeConnectivityType: Object.hasOwn(tapeConnectivityTypeLabels, rawState.tapeConnectivityType)
            ? rawState.tapeConnectivityType
            : 'sas',
        cdpReplicationRequired: replicationRequired === 'yes'
            ? sanitizeToggleState(rawState.cdpReplicationRequired, defaultToggleState.cdpReplicationRequired)
            : 'no',
        siemForwardingRequired: sanitizeToggleState(rawState.siemForwardingRequired, defaultToggleState.siemForwardingRequired),
        implDeliveryStyle: ['skeleton', 'white-glove'].includes(rawState.implDeliveryStyle) ? rawState.implDeliveryStyle : 'white-glove',
        advisorySupportRequired: sanitizeToggleState(rawState.advisorySupportRequired, 'no'),
        enablementRequired: sanitizeToggleState(rawState.enablementRequired, defaultToggleState.enablementRequired),
        uatRequired: sanitizeToggleState(rawState.uatRequired, defaultToggleState.uatRequired),
        asBuiltRequired: sanitizeToggleState(rawState.asBuiltRequired, defaultToggleState.asBuiltRequired),
        drRunbookRequired: sanitizeToggleState(rawState.drRunbookRequired, defaultToggleState.drRunbookRequired),
        sopRequired: sanitizeToggleState(rawState.sopRequired, 'no'),
        assumptionBandwidthAdequate: sanitizeToggleState(rawState.assumptionBandwidthAdequate, 'yes'),
        assumptionCustomerResourceAvailable: sanitizeToggleState(rawState.assumptionCustomerResourceAvailable, 'yes'),
        assumptionRemoteDeliveryAccepted: sanitizeToggleState(rawState.assumptionRemoteDeliveryAccepted, 'yes'),
        assumptionAdminAccessProvided: sanitizeToggleState(rawState.assumptionAdminAccessProvided, 'yes'),
        assumptionLicenceInPlace: sanitizeToggleState(rawState.assumptionLicenceInPlace, 'yes'),
        assumptionChangeControl: sanitizeToggleState(rawState.assumptionChangeControl, 'yes'),
        customRisksNotes: typeof rawState.customRisksNotes === 'string' ? rawState.customRisksNotes : '',
        vb365RepoMigrationScopes: sanitizeVb365RepoMigrationScopes(rawState.vb365RepoMigrationScopes),
        vbrRepoMigrationScopes:   sanitizeVbrRepoMigrationScopes(rawState.vbrRepoMigrationScopes),
        // Passed through as-is — applyEstimatorState validates shape before consuming.
        hvmEnabled:               typeof rawState.hvmEnabled === 'boolean' ? rawState.hvmEnabled : true,
        hypervisorMigrationScopes: Array.isArray(rawState.hypervisorMigrationScopes) ? rawState.hypervisorMigrationScopes : [],
        step2CanvasState:          sanitizeStep2CanvasState(rawState.step2CanvasState),
        step2MatrixState:          rawState.step2MatrixState && typeof rawState.step2MatrixState === 'object' ? rawState.step2MatrixState : null,
    };
}

// ── VB365 repository migration scope sanitisation ──────────────────────────────

const _validVb365RepoTypeValues = new Set(vb365RepoTypeOptions.map((o) => o.value));

function sanitizeVb365RepoMigrationScopes(rawScopes) {
    if (!Array.isArray(rawScopes)) return [];
    return rawScopes
        .filter((s) => s && typeof s === 'object')
        .map((s) => ({
            id:             parseInt(s.id, 10) || 0,
            targetRepoType: _validVb365RepoTypeValues.has(s.targetRepoType) ? s.targetRepoType : 'object-s3',
            // repoCount and m365WorkloadCount are no longer stored — derived from Step 1 at calc time.
        }));
}

// ── VBR repository migration scope sanitisation ───────────────────────────────

const _validVbrRepoSourceTypeValues = new Set(vbrRepoMigrationSourceTypeOptions.map((o) => o.value));
const _validVbrRepoTargetTypeValues  = new Set(['block-performance-tier', 'blob-performance-tier', 'vdc-vault-aws', 'vdc-vault-azure']);

function sanitizeVbrRepoMigrationScopes(rawScopes) {
    if (!Array.isArray(rawScopes)) return [];
    return rawScopes
        .filter((s) => s && typeof s === 'object')
        .map((s) => {
            // sourceRowId: integer rowId of the specific Step 1 repo workload, or null for
            // legacy scopes that only stored a category string in sourceType.
            const sourceRowId = Number.isInteger(s.sourceRowId) ? s.sourceRowId : null;
            const sourceType  = _validVbrRepoSourceTypeValues.has(s.sourceType)
                ? s.sourceType
                : (sourceRowId != null ? 'standalone' : 'block');
            return {
                id:                    parseInt(s.id, 10) || 0,
                sourceRowId,
                sourceType,
                targetType:            _validVbrRepoTargetTypeValues.has(s.targetType) ? s.targetType : 'block-performance-tier',
                // repositoryCount and dataSizeTb are no longer stored on the scope —
                // repository count is 1 per row, and data volume is derived from Step 1 workloads.
                includeHardeningSetup: s.includeHardeningSetup !== false,  // default true
            };
        });
}

// ── Step 2 canvas state sanitisation ─────────────────────────────────────────
// step2CanvasState was previously passed through raw. This function validates
// each lane array and per-lane fields so that corrupted or stale JSON files
// (e.g. missing vb365Lanes from pre-VB365-lanes exports, null fields, wrong
// types) never reach applyStep2CanvasState in a broken shape.

function sanitizeVbrLane(raw) {
    if (!raw || typeof raw !== 'object') return null;
    const laneId = raw.laneId != null ? String(raw.laneId) : null;
    if (!laneId) return null;
    const type = raw.type === 'existing' ? 'existing' : 'new';
    return {
        laneId,
        type,
        ...(type === 'existing' && raw.sourceInstanceId != null
            ? { sourceInstanceId: String(raw.sourceInstanceId) } : {}),
        name:           typeof raw.name === 'string' && raw.name ? raw.name : (type === 'existing' ? 'Reused VBR' : 'New VBR Server'),
        siteId:         parseInt(raw.siteId, 10) || 1,
        siteName:       typeof raw.siteName === 'string' ? raw.siteName : '',
        deploymentType: raw.deploymentType === 'physical' ? 'physical' : 'virtual',
        osPlatform:     raw.osPlatform === 'linux' ? 'linux' : 'windows',
        vsaHa:          raw.vsaHa === true,
        configExpanded: raw.configExpanded === true,
        step2Only:      raw.step2Only === true,
    };
}

function sanitizeCloudLane(raw) {
    if (!raw || typeof raw !== 'object') return null;
    const laneId = raw.laneId != null ? String(raw.laneId) : null;
    if (!laneId) return null;
    const validProviders = new Set(['aws', 'azure', 'gcp']);
    const cloudProvider = validProviders.has(raw.cloudProvider) ? raw.cloudProvider : null;
    if (!cloudProvider) return null;
    return {
        laneId,
        cloudProvider,
        name:     typeof raw.name === 'string' && raw.name ? raw.name : `${cloudProvider} Appliance`,
        siteId:   parseInt(raw.siteId, 10) || 1,
        siteName: typeof raw.siteName === 'string' ? raw.siteName : '',
    };
}

function sanitizeVb365Lane(raw) {
    if (!raw || typeof raw !== 'object') return null;
    const laneId = raw.laneId != null ? String(raw.laneId) : null;
    if (!laneId) return null;
    const type = raw.type === 'existing' ? 'existing' : 'new';
    return {
        laneId,
        type,
        ...(type === 'existing' && raw.sourceInstanceId != null
            ? { sourceInstanceId: String(raw.sourceInstanceId) } : {}),
        name:     typeof raw.name === 'string' && raw.name ? raw.name : (type === 'existing' ? 'Reused VB365' : 'New VB365 Server'),
        siteId:   parseInt(raw.siteId, 10) || 1,
        siteName: typeof raw.siteName === 'string' ? raw.siteName : '',
    };
}

function sanitizeStep2CanvasState(raw) {
    if (!raw || typeof raw !== 'object') return null;
    const vbrLanes    = (Array.isArray(raw.vbrLanes)    ? raw.vbrLanes    : []).map(sanitizeVbrLane).filter(Boolean);
    const cloudLanes  = (Array.isArray(raw.cloudLanes)  ? raw.cloudLanes  : []).map(sanitizeCloudLane).filter(Boolean);
    const vb365Lanes  = (Array.isArray(raw.vb365Lanes)  ? raw.vb365Lanes  : []).map(sanitizeVb365Lane).filter(Boolean);
    // assignments: { [rowId: string]: laneId: string } — keep only entries where both are strings/numbers
    const rawAssignments = raw.assignments && typeof raw.assignments === 'object' ? raw.assignments : {};
    const assignments = Object.fromEntries(
        Object.entries(rawAssignments)
            .filter(([k, v]) => !isNaN(parseInt(k, 10)) && v != null)
            .map(([k, v]) => [k, String(v)])
    );
    const nextLaneId = Number.isInteger(raw.nextLaneId) && raw.nextLaneId > 1 ? raw.nextLaneId : 1;
    return { vbrLanes, cloudLanes, vb365Lanes, assignments, nextLaneId };
}
